// SVG constants
export const NS = "http://www.w3.org/2000/svg";
export const INK = "#071013";
export const PINK = "#E80C89";

export const TOUCH_BREAKPOINT = 1250;
export const MOTION_EASE_POWER = 2.2;

// Utility functions
export function mix(a, b, t) {
    return a + (b - a) * t;
}

export function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

export function hexToRgb(hex) {
    const value = hex.replace("#", "");
    return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16),
    };
}

export function mixColor(a, b, t) {
    const ca = hexToRgb(a);
    const cb = hexToRgb(b);
    return `rgb(${Math.round(mix(ca.r, cb.r, t))}, ${Math.round(mix(ca.g, cb.g, t))}, ${Math.round(mix(ca.b, cb.b, t))})`;
}

export function createPathMorph(from, to) {
    const numberPattern = /-?\d*\.?\d+(?:e[-+]?\d+)?/gi;
    const fromParts = from.split(numberPattern);
    const fromNumbers = (from.match(numberPattern) || []).map(Number);
    const toNumbers = (to.match(numberPattern) || []).map(Number);
    const fromTemplate = from.replace(numberPattern, "#");
    const toTemplate = to.replace(numberPattern, "#");

    if (fromTemplate !== toTemplate || fromNumbers.length !== toNumbers.length) {
        return (t) => (t < 0.5 ? from : to);
    }

    return (t) => {
        let value = "";
        for (let i = 0; i < fromNumbers.length; i += 1) {
            value += `${fromParts[i]}${mix(fromNumbers[i], toNumbers[i], t)}`;
        }
        return value + fromParts[fromParts.length - 1];
    };
}

export function createSvg() {
    const svg = document.createElementNS(NS, "svg");
    svg.classList.add("animatedVisualSwap__visual");
    svg.setAttribute("viewBox", "0 0 320 320");
    svg.setAttribute("width", "100%");
    svg.setAttribute("height", "100%");
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    svg.setAttribute("role", "img");
    return svg;
}

export function attachInteractive(target, apply, options = {}) {
    let current = 0;
    let targetValue = 0;
    let raf = 0;

    const isTouchScreen = () =>
        typeof window !== "undefined" && window.innerWidth < TOUCH_BREAKPOINT;

    const isTouch = isTouchScreen();
    const enterSpeed = isTouch
        ? (options.touchSpeed ?? 0.07)
        : (options.speed ?? 0.12);
    const leaveSpeed = isTouch
        ? (options.touchLeaveSpeed ?? options.touchSpeed ?? 0.07)
        : (options.leaveSpeed ?? options.speed ?? 0.12);

    function tick() {
        const speed = targetValue > current ? enterSpeed : leaveSpeed;
        current += (targetValue - current) * speed;
        if (Math.abs(targetValue - current) < 0.001) current = targetValue;
        apply(current);
        if (current !== targetValue) {
            raf = requestAnimationFrame(tick);
        } else {
            raf = 0;
        }
    }

    function setTarget(value) {
        targetValue = value;
        if (!raf) raf = requestAnimationFrame(tick);
    }

    apply(0);

    if (isTouchScreen()) {
        let triggered = false;
        const observer = new IntersectionObserver(
            (entries) => {
                if (triggered) return;
                const entry = entries[0];
                if (entry.isIntersecting) {
                    triggered = true;
                    setTarget(1);
                    observer.disconnect();
                }
            },
            { threshold: 1.0 },
        );
        observer.observe(target);

        return () => {
            observer.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }

    const onEnter = () => setTarget(1);
    const onLeave = () => setTarget(0);

    target.addEventListener("mouseenter", onEnter);
    target.addEventListener("mouseleave", onLeave);
    target.addEventListener("focusin", onEnter);
    target.addEventListener("focusout", onLeave);

    return () => {
        target.removeEventListener("mouseenter", onEnter);
        target.removeEventListener("mouseleave", onLeave);
        target.removeEventListener("focusin", onEnter);
        target.removeEventListener("focusout", onLeave);
        if (raf) cancelAnimationFrame(raf);
    };
}

export function easeMotion(progress) {
    return 1 - Math.pow(1 - clamp(progress, 0, 1), MOTION_EASE_POWER);
}

export function pointsPath(points) {
    return points.map((point, index) => `${index === 0 ? "M" : "L"}${point[0]} ${point[1]}`).join("");
}

export function mixPoints(from, to, progress) {
    return from.map((point, index) => [
        mix(point[0], to[index][0], progress),
        mix(point[1], to[index][1], progress),
    ]);
}

export function createMotionSvg() {
    return createSvg();
}

export function setDashedStroke(node) {
    node.setAttribute("fill", "none");
    node.setAttribute("stroke", INK);
    node.setAttribute("stroke-opacity", "0.5");
    node.setAttribute("stroke-width", "4");
    node.setAttribute("stroke-linecap", "round");
    node.setAttribute("stroke-linejoin", "round");
    node.setAttribute("stroke-dasharray", "1 12");
}

export function setMotionPaint(nodes, progress) {
    const stroke = mixColor(INK, PINK, progress);
    const opacity = mix(0.5, 1, progress);
    nodes.forEach((node) => {
        if (node.hasAttribute("stroke") || node.getAttribute("fill") === "none") {
            node.setAttribute("stroke", stroke);
            node.setAttribute("stroke-opacity", String(opacity));
        }
        if (node.hasAttribute("fill") && node.getAttribute("fill") !== "none") {
            node.setAttribute("fill", stroke);
            node.setAttribute("fill-opacity", String(opacity));
        }
    });
}

export function createMotionLine(parent, x1, y1, x2, y2) {
    const line = document.createElementNS(NS, "line");
    line.setAttribute("x1", String(x1));
    line.setAttribute("y1", String(y1));
    line.setAttribute("x2", String(x2));
    line.setAttribute("y2", String(y2));
    setDashedStroke(line);
    parent.appendChild(line);
    return line;
}

export function createMotionEllipse(parent, cx, cy, rx, ry) {
    const ellipse = document.createElementNS(NS, "ellipse");
    ellipse.setAttribute("cx", String(cx));
    ellipse.setAttribute("cy", String(cy));
    ellipse.setAttribute("rx", String(rx));
    ellipse.setAttribute("ry", String(ry));
    setDashedStroke(ellipse);
    parent.appendChild(ellipse);
    return ellipse;
}

export function createMotionPath(parent, d) {
    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", d);
    setDashedStroke(path);
    parent.appendChild(path);
    return path;
}

export function createStrokePath(parent) {
    return createMotionPath(parent, "");
}

export function createStrokeRect(parent) {
    const rect = document.createElementNS(NS, "rect");
    rect.setAttribute("width", "68.5714");
    rect.setAttribute("height", "68.5714");
    rect.setAttribute("rx", "4");
    setDashedStroke(rect);
    parent.appendChild(rect);
    return rect;
}

export function phenomenonCrescentPath(sideCx, sideCy, sideR, innerCx, innerCy, direction) {
    const distance = Math.abs(innerCx - sideCx);
    const x = (sideCx + innerCx) / 2;
    const yOffset = Math.sqrt(Math.max(sideR * sideR - (distance * distance) / 4, 0));
    const topY = sideCy - yOffset;
    const bottomY = sideCy + yOffset;

    if (direction === "left") {
        return `M${x} ${topY}A${sideR} ${sideR} 0 1 0 ${x} ${bottomY}`;
    }

    return `M${x} ${topY}A${sideR} ${sideR} 0 1 1 ${x} ${bottomY}`;
}

export function quadrantPath(cx, cy, r, quadrant) {
    const points = [
        { x: cx - r, y: cy, ex: cx, ey: cy - r },
        { x: cx, y: cy - r, ex: cx + r, ey: cy },
        { x: cx + r, y: cy, ex: cx, ey: cy + r },
        { x: cx, y: cy + r, ex: cx - r, ey: cy },
    ][quadrant];

    return `M${cx} ${cy}L${points.x} ${points.y}A${r} ${r} 0 0 1 ${points.ex} ${points.ey}L${cx} ${cy}Z`;
}

export function academicTrianglePath(topY, bottomY, leftX = 2, rightX = 176.449, centerX = 89.2247, assetHeight = 244) {
    const visualTopY = assetHeight - bottomY;
    const visualBottomY = assetHeight - topY;
    return `M${centerX} ${visualTopY}L${rightX} ${visualBottomY}H${leftX}L${centerX} ${visualTopY}Z`;
}

export function starPoints(cx, cy, outerR, innerR, points, rotationDeg = 0) {
    const resultPoints = [];
    const angleSlice = (Math.PI * 2) / (points * 2);
    const rotationRad = (rotationDeg * Math.PI) / 180;

    for (let i = 0; i < points * 2; i += 1) {
        const angle = i * angleSlice + rotationRad;
        const radius = i % 2 === 0 ? outerR : innerR;
        resultPoints.push([cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)]);
    }
    return resultPoints;
}
